import { axisBottom, axisRight } from 'd3-axis'
import { easeLinear, easeExp } from 'd3-ease'
import { scaleOrdinal } from 'd3-scale'
import { select } from 'd3-selection'
import * as d3_shape from 'd3-shape'
import { timeFormat } from 'd3-time-format'
import { transition } from 'd3-transition'
import PropTypes from 'prop-types'
import { Component } from 'react'

import Text from 'components/common/Text'
import theme from 'theme'

export default class BaseGraph extends Component {
  static propTypes = {
    colorMap: PropTypes.object,
    displaySingleData: PropTypes.bool,
    height: PropTypes.number,
    overrideDefaultSvgHeight: PropTypes.bool,
    reportData: PropTypes.array.isRequired,
    rootId: PropTypes.string.isRequired,
    translate: PropTypes.object,
    width: PropTypes.number
  }

  static defaultProps = {
    displaySingleData: false
  }

  borderColor = theme.colors.border[0]
  activeColor = theme.colors.green[0]
  revokedColor = theme.colors.red[0]
  svgHeight = 260
  // Dictates the number of ticks on the vertical axis
  margin = { top: 20, right: 20, bottom: 20, left: 20 }
  yAxisTickCount = 5
  pointSize = 100
  tooltipHeight = 50
  tooltipWidth = 130
  transition = transition().duration(800).ease(easeExp)

  constructor() {
    super()
    this.state = {
      error: false
    }
  }

  colors(color) {
    if (this.props.colorMap) return this.props.colorMap[color]

    return scaleOrdinal([
      theme.colors.green[0],
      theme.colors.green[1],
      theme.colors.blue[0],
      theme.colors.blue[0],
      theme.colors.purple[0],
      theme.colors.purple[1],
      theme.colors.red[0],
      theme.colors.red[1],
      theme.colors.yellow[0],
      '#F57300',
      theme.colors.steel[1],
      theme.colors.steel[2]
    ])(color)
  }

  setReportBasis(report, yMin, yMax) {
    const { margin } = this
    // This components SVG tag

    // TODO - we can accept a prop like `size` to standardize the dimensions of the graphs
    const svg = select(this.props.rootId).attr(
      'height',
      this.props.overrideDefaultSvgHeight ? this.props.height : this.svgHeight
    )

    this.props.width &&
      select(this.props.rootId).attr('width', this.props.width)
    // The dimensions of the '.entire-group' chart group (taking into account margins)
    const width = this.svgDimension(svg, 'width') - margin.left - margin.right
    const height = this.svgDimension(svg, 'height') - margin.top - margin.bottom

    // remove whatever was previously drawn for the chart
    // (this leaves the tooltip portion in place)
    svg.select('.entire-group').remove()

    // The '.entire-group' chart group
    const g = svg
      .append('g')
      .lower() // make sure this is rendered before the tooltips so those appear on top
      .attr(
        'transform',
        `translate(${this.props.translate?.left ?? margin.left}, ${
          this.props.translate?.top ?? margin.top
        })`
      )
      .attr('class', 'entire-group')

    // Calculate boundaries of y-axis as well as the tick values
    let ticks = this.tickValues(yMin, yMax, this.yAxisTickCount)

    return {
      svg,
      width,
      height,
      g,
      ticks,
      yMin,
      yMax
    }
  }

  svgDimension(svg, dimension) {
    return parseInt(svg.style(dimension), 10)
  }

  tickValues(min, max, numTicks) {
    return Array(numTicks)
      .fill()
      .map((_, i) => min + i * Math.ceil((max - min) / (numTicks - 1)))
  }

  // Tear down the svg and repaint, everytime the data changes
  redraw() {
    this.draw()
  }

  resize = () => {
    select(this.props.rootId).selectAll('*').remove()
    this.redraw()
  }

  componentDidMount() {
    if (this.props.reportData.length < 2 && !this.props.displaySingleData) {
      this.setState({ error: true })
    } else if (this.props.reportData.length > 0) {
      this.draw()
      window.addEventListener('resize', this.resize)
    }
  }

  componentWillUnmount() {
    // TODO, fix event listener logic here
    window.removeEventListener('resize', this.resize)
  }

  clearToolTip() {
    select(`${this.props.rootId} .tooltip-rect-group`)
      .transition()
      .duration(0)
      .attr('opacity', 0)

    select(`${this.props.rootId} .triangle-group`)
      .transition()
      .duration(0)
      .attr('opacity', 0)
  }

  drawToolTip() {
    const textColor = '#c8c8c8', // $color-grey
      bgOpacity = 0.8, // Opacity for visible
      rectHeight = this.tooltipHeight,
      rectWidth = this.tooltipWidth,
      circRadius = 5,
      textFontSize = 11

    let tooltip = select(this.props.rootId)
      .append('g')
      .attr('class', 'tooltip-group')

    let rectGroup = tooltip
      .append('g')
      .attr('class', 'tooltip-rect-group')
      .attr('opacity', 0)
      .attr('transform', `translate(${this.tooltipWidth}, 0)`)

    // tooltip background rectangle
    rectGroup
      .append('rect')
      .attr('height', rectHeight)
      .attr('width', rectWidth)
      .attr('fill-opacity', bgOpacity)
      .attr('stroke-opacity', bgOpacity)

    // tooltip color identifier
    rectGroup
      .append('circle')
      .attr('class', 'ttp-color')
      .attr('r', circRadius)
      .attr('transform', 'translate(15, 15)')

    //Tooltip text-key
    rectGroup
      .append('text')
      .attr('class', 'ttp-text-key')
      .attr('fill', textColor)
      .attr('font-size', textFontSize)
      .attr('alignment-baseline', 'central')
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(70, 15)')

    // Tooltip text-value
    rectGroup
      .append('text')
      .attr('class', 'ttp-text-value')
      .text('16')
      .attr('fill', textColor)
      .attr('font-size', textFontSize)
      .attr('text-anchor', 'middle')
      .attr('transform', `translate(${rectWidth / 2}, 35)`)

    let triangle = d3_shape.symbol().type(d3_shape.symbolTriangle)

    tooltip
      .append('g')
      .attr('class', 'triangle-group')
      .attr('opacity', 0)
      .append('path')
      .attr('class', 'tooltip-triangle')
      .attr('d', triangle)
      .attr('fill-opacity', bgOpacity)
      .attr('stroke-opacity', bgOpacity)
      .attr('size', 120)

    return tooltip
  }

  updateToolTip(event, data) {
    const { tooltipHeight, tooltipWidth } = this

    const svgWidth = select(this.props.rootId)
      .node()
      .getBoundingClientRect().width // find width of the whole svg
    const posX = tooltipWidth / 2
    const rectMargin = 20
    const triangleOffset = 3.5
    const t = transition().duration(200).ease(easeLinear)

    const chooseSideY = (offset) =>
      offset < tooltipHeight + rectMargin
        ? offset + rectMargin
        : offset - (tooltipHeight + rectMargin)

    const trianglePos = (offset) =>
      offset < tooltipHeight + rectMargin
        ? offset + rectMargin - triangleOffset
        : offset - rectMargin + triangleOffset

    const triangleRotate = (offset) =>
      offset < tooltipHeight + rectMargin ? 0 : 180

    const chooseX = (offset) => {
      if (offset < posX) {
        return 0 // If the point is off to the left, bump the tooltip rectange to the right
      } else if (svgWidth - tooltipWidth / 2 < offset) {
        return svgWidth - tooltipWidth // if the point is on the right, bump rect to the left
      } else {
        return offset - posX
      }
    }

    const tooltipGroup = select(`${this.props.rootId} .tooltip-rect-group`)
      .attr(
        'transform',
        `translate(${chooseX(event.offsetX)}, ${chooseSideY(event.offsetY)})`
      )
      .transition(t)
      .attr('opacity', 1)

    select(`${this.props.rootId} .triangle-group`)
      .attr(
        'transform',
        `translate(${event.offsetX}, ${trianglePos(
          event.offsetY
        )}) rotate(${triangleRotate(event.offsetY)})`
      )
      .transition(t)
      .attr('opacity', 1)

    select(`${this.props.rootId} .ttp-text-key`).text(
      this.formatTitle(data.gremlin)
    )

    select(`${this.props.rootId} .ttp-color`).attr(
      'fill',
      this.colors(data.gremlin)
    )

    select(`${this.props.rootId} .ttp-text-value`).text(data.count)

    return tooltipGroup
  }

  drawLegend(context, data) {
    const legendGroup = context.selectAll().data(data).enter().append('g')

    legendGroup
      .append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', 8)
      .attr('fill', (d) => this.colors(d))

    legendGroup
      .append('text')
      .text((d) => this.formatTitle(d))
      .attr('x', 14)
      .attr('y', 7)

    return legendGroup
  }

  formatTitle(string) {
    return string.toUpperCase().replace('_', ' ')
  }

  // Add the X Axis
  addXAxis(g, height, x) {
    return g
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call((g) => {
        g.call(axisBottom(x).tickFormat(timeFormat('%_m-%d')))
        g.selectAll('line').attr('class', 'tick xaxis').style('display', 'none')
      })
  }

  addYAxis(g, y, ticks, width, axisMargin) {
    return g.append('g').call((g) => {
      g.call(axisRight(y).tickValues(ticks).tickSize(width))
      g.selectAll('line')
        .style('stroke', this.borderColor)
        .style('stroke-dasharray', '2,2')
        .attr('class', 'tick yaxis')
      g.selectAll('.tick text').attr('x', -axisMargin)
    })
  }

  render() {
    return this.state.error ? (
      <Text
        color="error"
        css={{ textAlign: 'center', paddingTop: theme.space[3] }}
      >
        Not enough data to display report
      </Text>
    ) : (
      <svg
        css={{ color: theme.colors.steel[1] }}
        id={this.props.rootId.split('#')[1]}
      >
        {' '}
      </svg>
    )
  }
}
